import { useState } from 'react';
import { ArrowRightLeft, Truck, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useFeaturePermission } from '@/hooks/useFeaturePermission';
import { usePermissions } from '@/hooks/usePermissions';
import { useTransfersList, StockTransfer } from '@/hooks/useStockTransfers';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TransferDispatchModal } from './TransferDispatchModal';
import { TransferReceiveModal } from './TransferReceiveModal';

function mapOrgOpenTransferRow(r: Record<string, unknown>): StockTransfer {
  const id = String(r.id);
  return {
    id,
    document_number: String(r.document_number ?? ''),
    company_id_from: String(r.company_id_from ?? ''),
    company_id_to: String(r.company_id_to ?? ''),
    deposit_id_from: String(r.deposit_id_from ?? ''),
    deposit_id_to: String(r.deposit_id_to ?? ''),
    status: r.status as StockTransfer['status'],
    requested_by: '',
    requested_at: String(r.created_at ?? ''),
    approved_by: null,
    approved_at: null,
    dispatched_by: null,
    dispatched_at: (r.dispatched_at as string | null) ?? null,
    dispatch_signature_data: null,
    dispatch_signature_name: null,
    dispatched_by_name: null,
    delivered_by: null,
    received_by: null,
    received_at: null,
    receive_signature_data: null,
    receive_signature_name: null,
    received_by_name: null,
    notes: null,
    has_divergence: false,
    company_id: String(r.company_id_from ?? ''),
    created_at: String(r.created_at ?? ''),
    company_from: {
      id: String(r.company_id_from ?? ''),
      name: String(r.company_from_name ?? ''),
    },
    company_to: {
      id: String(r.company_id_to ?? ''),
      name: String(r.company_to_name ?? ''),
    },
  };
}

/**
 * Widget "Transferências Pendentes" para o painel de tarefas.
 * Mostra:
 *  - Solicitações recebidas aguardando despacho (empresa origem = ativa)
 *  - Transferências em trânsito aguardando recebimento (empresa destino = ativa)
 * Gate duplo: enabled apenas se canAccess('org:manage_transfers').
 * groupScope: visão consolidada do grupo (RPC), protegida por org:view_group_finance.
 */
export function PendingTransfersWidget({ groupScope = false }: { groupScope?: boolean }) {
  const { company } = useAuth();
  const { canAccess } = usePermissions();
  const { hasPermission: canManageTransfers } = useFeaturePermission('org:manage_transfers');

  const orgTransfersQuery = useQuery({
    queryKey: ['org-open-stock-transfers'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_org_open_stock_transfers');
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map(mapOrgOpenTransferRow);
    },
    enabled: groupScope && canAccess('org:view_group_finance'),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: solicitadas = [], isLoading: loadingSol } = useTransfersList({
    status: 'solicitado',
    skip: groupScope,
  });
  const { data: emTransito = [], isLoading: loadingTrans } = useTransfersList({
    status: 'em_transito',
    skip: groupScope,
  });

  const [selectedTransfer, setSelectedTransfer] = useState<StockTransfer | null>(null);
  const [activeModal, setActiveModal] = useState<'dispatch' | 'receive' | null>(null);

  const canShow = groupScope ? canAccess('org:view_group_finance') : canManageTransfers;
  if (!canShow) return null;

  const listSolicitado = groupScope
    ? (orgTransfersQuery.data ?? []).filter((t) => t.status === 'solicitado' || t.status === 'aprovado')
    : solicitadas;
  const listTransito = groupScope ? (orgTransfersQuery.data ?? []).filter((t) => t.status === 'em_transito') : emTransito;

  const pendingDispatch = groupScope
    ? listSolicitado
    : listSolicitado.filter((t) => t.company_id_from === company?.id);
  const pendingReceive = groupScope
    ? listTransito
    : listTransito.filter((t) => t.company_id_to === company?.id);

  const total = pendingDispatch.length + pendingReceive.length;

  const loadingOrg = groupScope && orgTransfersQuery.isLoading;
  if (total === 0 && !loadingSol && !loadingTrans && !loadingOrg) return null;

  const isLoading = loadingSol || loadingTrans || loadingOrg;

  return (
    <>
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-blue-600" />
            Transferências Pendentes
            {total > 0 && (
              <Badge className="ml-auto bg-blue-600 text-white text-xs">
                {total}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando transferências...
            </div>
          )}

          {/* Aguardando despacho */}
          {pendingDispatch.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">
                Aguardando Despacho ({pendingDispatch.length})
              </p>
              {pendingDispatch.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between p-3 bg-white border border-yellow-200 rounded-lg"
                >
                  <div className="space-y-0.5">
                    <p className="font-mono text-sm font-semibold text-blue-700">{t.document_number}</p>
                    <p className="text-xs text-muted-foreground">
                      Para: <strong>{t.company_to?.name}</strong>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(t.created_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs gap-1 border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                    onClick={() => { setSelectedTransfer(t); setActiveModal('dispatch'); }}
                  >
                    <Truck className="h-3 w-3" />
                    Despachar
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Aguardando recebimento */}
          {pendingReceive.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                Em Trânsito — Aguardando Recebimento ({pendingReceive.length})
              </p>
              {pendingReceive.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between p-3 bg-white border border-purple-200 rounded-lg"
                >
                  <div className="space-y-0.5">
                    <p className="font-mono text-sm font-semibold text-blue-700">{t.document_number}</p>
                    <p className="text-xs text-muted-foreground">
                      De: <strong>{t.company_from?.name}</strong>
                    </p>
                    {t.dispatched_at && (
                      <p className="text-xs text-muted-foreground">
                        Despachado em {format(new Date(t.dispatched_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-1 bg-green-600 hover:bg-green-700"
                    onClick={() => { setSelectedTransfer(t); setActiveModal('receive'); }}
                  >
                    Receber
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedTransfer && activeModal === 'dispatch' && (
        <TransferDispatchModal
          transfer={selectedTransfer}
          open
          onOpenChange={(open) => {
            if (!open) { setActiveModal(null); setSelectedTransfer(null); }
          }}
        />
      )}

      {selectedTransfer && activeModal === 'receive' && (
        <TransferReceiveModal
          transfer={selectedTransfer}
          open
          onOpenChange={(open) => {
            if (!open) { setActiveModal(null); setSelectedTransfer(null); }
          }}
        />
      )}
    </>
  );
}
